import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { publicApi } from '../services/api';
import { formatZoned } from '../context/SettingsContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Package,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Wrench,
  MapPin,
  Camera,
  Send,
  Loader2,
  X,
  ChevronDown,
  ChevronUp,
  FileText,
  Shield,
  Search,
  Cpu
} from 'lucide-react';
import { toast } from 'sonner';
import { PublicInspectionRunner } from '../components/PublicInspectionRunner';

const statusStyles = {
  active: { bg: 'bg-[#FFD108]/20', text: 'text-[#FFD108]', label: 'Active' },
  in_maintenance: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'In Maintenance' },
  out_of_service: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Out of Service' },
};

const severityStyles = {
  critical: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500' },
  high: { bg: 'bg-orange-500/20', text: 'text-orange-400', border: 'border-orange-500' },
  medium: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500' },
  low: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500' },
};

const PublicAssetPage = () => {
  const { code } = useParams();
  const [loading, setLoading] = useState(true);
  const [asset, setAsset] = useState(null);
  const [error, setError] = useState(null);
  const [showReport, setShowReport] = useState(false);

  // Report form state
  const [reportTitle, setReportTitle] = useState('');
  const [reportDesc, setReportDesc] = useState('');
  const [reportSeverity, setReportSeverity] = useState('medium');
  const [reporterName, setReporterName] = useState('');
  const [reporterEmail, setReporterEmail] = useState('');
  const [reporterPhone, setReporterPhone] = useState('');
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [defectNumber, setDefectNumber] = useState('');

  // Error code lookup state (Phase 51)
  const [errorCodeInput, setErrorCodeInput] = useState('');
  const [errorLookupLoading, setErrorLookupLoading] = useState(false);
  const [errorLookupResult, setErrorLookupResult] = useState(null);
  const [errorLookupError, setErrorLookupError] = useState(null);

  // Phase 52: inspection templates the admin has whitelisted for this asset.
  const [publicInspectionTemplates, setPublicInspectionTemplates] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await publicApi.getAsset(code);
        setAsset(res.data);
        // Fire-and-forget: the templates list is a separate endpoint so the
        // public asset response stays small (templates can include big
        // checklists). Failures here are silent — runner just doesn't show.
        try {
          const tres = await publicApi.listInspectionTemplates(code);
          setPublicInspectionTemplates(Array.isArray(tres.data) ? tres.data : []);
        } catch {
          /* silent */
        }
      } catch (err) {
        if (err.response?.status === 403) {
          setError('Public access is not enabled for this asset.');
        } else if (err.response?.status === 404) {
          setError('Asset not found. The QR code may be invalid.');
        } else {
          setError('Failed to load asset information.');
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [code]);

  const handlePhotoCapture = (e) => {
    Array.from(e.target.files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => setPhotos(prev => [...prev, ev.target.result]);
      reader.readAsDataURL(file);
    });
  };

  // Normalises an AU mobile to canonical "04XX XXX XXX" form.
  // Accepts user input with spaces, dashes, or +61 country code. Returns
  // null if the digits don't constitute a valid AU mobile (10 digits
  // starting with 04, OR 11 digits starting with 614 / +614).
  const normaliseAuMobile = (raw) => {
    if (!raw) return null;
    let digits = String(raw).replace(/[^0-9+]/g, '');
    if (digits.startsWith('+61')) digits = '0' + digits.slice(3);
    else if (digits.startsWith('61') && digits.length === 11) digits = '0' + digits.slice(2);
    digits = digits.replace(/[^0-9]/g, '');
    if (digits.length !== 10 || !digits.startsWith('04')) return null;
    return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`;
  };

  const handleSubmitReport = async (e) => {
    e.preventDefault();
    if (!reportTitle.trim() || !reportDesc.trim()) {
      toast.error('Title and description are required');
      return;
    }
    if (!reporterName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    const formattedPhone = normaliseAuMobile(reporterPhone);
    if (!formattedPhone) {
      toast.error('Enter a valid Australian mobile (e.g. 0414 320 847)');
      return;
    }
    setSubmitting(true);
    try {
      const res = await publicApi.reportIssue(code, {
        title: reportTitle.trim(),
        description: reportDesc.trim(),
        severity: reportSeverity,
        reporter_name: reporterName.trim(),
        reporter_phone: formattedPhone,
        reporter_email: reporterEmail.trim() || undefined,
        photos,
      });
      setDefectNumber(res.data.defect_number);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleErrorCodeLookup = async (e) => {
    e?.preventDefault();
    const trimmed = errorCodeInput.trim();
    if (!trimmed) {
      toast.error('Enter an error code shown on the equipment display');
      return;
    }
    setErrorLookupLoading(true);
    setErrorLookupError(null);
    setErrorLookupResult(null);
    try {
      const res = await publicApi.lookupErrorCode(code, trimmed);
      setErrorLookupResult(res.data);
    } catch (err) {
      setErrorLookupError(err.response?.data?.detail || 'Lookup failed. Please try again.');
    } finally {
      setErrorLookupLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#FFD108] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#121212] flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-[#1C1C1C] border-[#2E2E2E]">
          <CardContent className="p-8 text-center">
            <Shield className="w-12 h-12 text-[#2E2E2E] mx-auto mb-4" />
            <h1 className="text-xl font-heading font-bold text-white mb-2">Access Unavailable</h1>
            <p className="text-[hsl(0,0%,63%)] text-sm">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const status = statusStyles[asset.status] || statusStyles.active;

  return (
    <div className="min-h-screen bg-[#121212]" data-testid="public-asset-page">

      {/* Header Bar */}
      <div className="bg-[#1C1C1C] border-b border-[#2E2E2E] px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          {asset.company_logo ? (
            <div className="w-9 h-9 rounded-lg bg-white/95 flex items-center justify-center overflow-hidden shrink-0" data-testid="public-company-logo">
              <img src={asset.company_logo} alt={asset.tenant_name || 'Company'} className="w-full h-full object-contain p-0.5" />
            </div>
          ) : (
            <div className="w-8 h-8 bg-[#FFD108]/20 rounded-lg flex items-center justify-center shrink-0">
              <Package className="w-4 h-4 text-[#FFD108]" />
            </div>
          )}
          <div>
            <p className="text-white font-heading font-semibold text-sm">{asset.tenant_name || 'EQUIPR Guardian'}</p>
            <p className="text-[hsl(0,0%,63%)] text-xs">Public Asset Information</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 pb-8">
        {/* Asset Identity Card */}
        <Card className="bg-[#1C1C1C] border-[#2E2E2E] mb-4" data-testid="asset-identity-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h1 className="text-2xl font-heading font-bold text-white">
                  {asset.unit_number || asset.name}
                </h1>
                {asset.unit_number && asset.name && (
                  <p className="text-[hsl(0,0%,63%)] text-sm mt-1">{asset.name}</p>
                )}
              </div>
              <Badge className={`${status.bg} ${status.text} border-0 font-medium`}>
                {status.label}
              </Badge>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              {asset.make && (
                <div>
                  <p className="label-overline mb-0.5">Make</p>
                  <p className="text-white">{asset.make}</p>
                </div>
              )}
              {asset.model && (
                <div>
                  <p className="label-overline mb-0.5">Model</p>
                  <p className="text-white">{asset.model}</p>
                </div>
              )}
              {asset.year && (
                <div>
                  <p className="label-overline mb-0.5">Year</p>
                  <p className="text-white font-mono">{asset.year}</p>
                </div>
              )}
              {asset.serial_number && (
                <div>
                  <p className="label-overline mb-0.5">Serial</p>
                  <p className="text-white font-mono text-xs">{asset.serial_number}</p>
                </div>
              )}
              {asset.category && (
                <div>
                  <p className="label-overline mb-0.5">Category</p>
                  <p className="text-white">{asset.category}</p>
                </div>
              )}
              {asset.depot_name && (
                <div>
                  <p className="label-overline mb-0.5">Home depot</p>
                  <p className="text-white flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-[hsl(0,0%,63%)]" />
                    {asset.depot_name}
                  </p>
                </div>
              )}
              {asset.last_position && (
                <div data-testid="public-asset-live-position">
                  <p className="label-overline mb-0.5">Live position</p>
                  <a
                    href={`https://www.google.com/maps?q=${asset.last_position.latitude},${asset.last_position.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-volt flex items-center gap-1 underline-offset-2 hover:underline font-mono text-xs"
                    title="Open in Google Maps"
                  >
                    <MapPin className="w-3 h-3" />
                    {asset.last_position.latitude.toFixed(5)}, {asset.last_position.longitude.toFixed(5)}
                  </a>
                  {asset.last_position.recorded_at && (
                    <p className="text-[10px] text-[hsl(0,0%,55%)] mt-0.5">
                      as of {new Date(asset.last_position.recorded_at).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Last Inspection */}
        <Card className="bg-[#1C1C1C] border-[#2E2E2E] mb-4" data-testid="last-inspection-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Wrench className="w-4 h-4 text-[hsl(0,0%,63%)]" />
              <h2 className="label-overline">Last Inspection</h2>
            </div>

            {asset.last_inspection ? (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  {asset.last_inspection.status === 'passed' ? (
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                  <span className={`font-heading font-semibold ${
                    asset.last_inspection.status === 'passed' ? 'text-green-400' : 'text-red-400'
                  }`}>
                    {asset.last_inspection.status === 'passed' ? 'PASSED' : 'FAILED'}
                  </span>
                </div>
                <div className="text-sm text-[hsl(0,0%,63%)] space-y-1">
                  {asset.last_inspection.template && (
                    <p>{asset.last_inspection.template}</p>
                  )}
                  <p className="font-mono text-xs">
                    {formatZoned(asset.last_inspection.date, asset.timezone, 'd MMM yyyy')}
                  </p>
                  <p className="text-xs">
                    {asset.last_inspection.passed} passed, {asset.last_inspection.failed} failed
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-[hsl(0,0%,63%)] text-sm">No inspections recorded</p>
            )}
          </CardContent>
        </Card>

        {/* Open Defects */}
        <Card className="bg-[#1C1C1C] border-[#2E2E2E] mb-4" data-testid="defects-summary-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-[hsl(0,0%,63%)]" />
              <h2 className="label-overline">Open Defects</h2>
            </div>

            {asset.defects.total_open > 0 ? (
              <div>
                <p className="text-2xl font-heading font-bold text-white mb-2">
                  {asset.defects.total_open}
                </p>
                <div className="flex flex-wrap gap-2">
                  {asset.defects.critical > 0 && (
                    <Badge className="bg-red-500/20 text-red-400 border-0">
                      {asset.defects.critical} Critical
                    </Badge>
                  )}
                  {asset.defects.high > 0 && (
                    <Badge className="bg-orange-500/20 text-orange-400 border-0">
                      {asset.defects.high} High
                    </Badge>
                  )}
                  {asset.defects.medium > 0 && (
                    <Badge className="bg-yellow-500/20 text-yellow-400 border-0">
                      {asset.defects.medium} Medium
                    </Badge>
                  )}
                  {asset.defects.low > 0 && (
                    <Badge className="bg-blue-500/20 text-blue-400 border-0">
                      {asset.defects.low} Low
                    </Badge>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-green-400 text-sm flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                No open defects
              </p>
            )}
          </CardContent>
        </Card>

        {/* Geofence trust signal — visible only when the tenant has at least
            one active fence. Reassures hire customers that the unit is on
            site (green) or warns them it has strayed (amber). */}
        {asset.geofence_status?.tracked && (
          <Card className="bg-[#1C1C1C] border-[#2E2E2E] mb-4" data-testid="geofence-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="w-4 h-4 text-[hsl(0,0%,63%)]" />
                <h2 className="label-overline">Tracked Location</h2>
              </div>
              {asset.geofence_status.inside.length > 0 ? (
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-emerald-400 text-sm font-semibold">Inside designated area</p>
                    <p className="text-white text-sm mt-1 truncate" title={asset.geofence_status.inside.join(', ')}>
                      {asset.geofence_status.inside.join(' · ')}
                    </p>
                    <p className="text-[hsl(0,0%,63%)] text-xs mt-1">
                      Verified by live GPS telematics.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full bg-amber-500/15 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-4 h-4 text-amber-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-amber-400 text-sm font-semibold">Outside designated area</p>
                    <p className="text-[hsl(0,0%,63%)] text-xs mt-1">
                      The owner has been notified that this asset is currently outside its tracked zones.
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Next Maintenance */}
        {asset.next_maintenance && (
          <Card className="bg-[#1C1C1C] border-[#2E2E2E] mb-4" data-testid="maintenance-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Clock className="w-4 h-4 text-[hsl(0,0%,63%)]" />
                <h2 className="label-overline">Upcoming Maintenance</h2>
              </div>
              <p className="text-white text-sm font-medium">{asset.next_maintenance.type}</p>
              <p className="text-[hsl(0,0%,63%)] font-mono text-xs mt-1">
                Due: {formatZoned(asset.next_maintenance.due_date, asset.timezone, 'd MMM yyyy')}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Phase 50: Public documents + certificates surfaced from owner-tenant.
            Only ever rendered when an admin has explicitly toggled each item
            public — defaults are off. Files served through the public API
            so the tenant doesn't need to expose Azure Blob URLs directly. */}
        {((asset.public_documents && asset.public_documents.length > 0) ||
          (asset.public_certificates && asset.public_certificates.length > 0)) && (
          <Card className="bg-[#1C1C1C] border-[#2E2E2E] mb-4" data-testid="public-docs-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-[hsl(0,0%,63%)]" />
                <h2 className="label-overline">Documents &amp; Certificates</h2>
              </div>
              <div className="space-y-2">
                {(asset.public_certificates || []).map((c) => {
                  const expiry = c.expiry_date ? new Date(c.expiry_date) : null;
                  const expired = expiry && expiry < new Date();
                  return (
                    <a
                      key={`cert-${c.id}`}
                      href={publicApi.certificateFileUrl(code, c.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between p-3 rounded-lg bg-[#262626] border border-[#2E2E2E] hover:border-[#FFD108]/40 transition-colors"
                      data-testid={`public-cert-${c.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">{c.certificate_type}</p>
                        <p className="text-[hsl(0,0%,63%)] font-mono text-[11px]">
                          {c.cert_number}
                          {c.issuing_body ? ` · ${c.issuing_body}` : ''}
                          {expiry ? ` · ${expired ? 'Expired ' : 'Expires '}${formatZoned(c.expiry_date, asset.timezone, 'd MMM yyyy')}` : ''}
                        </p>
                      </div>
                      <FileText className={`w-4 h-4 flex-shrink-0 ${expired ? 'text-red-400' : 'text-[#FFD108]'}`} />
                    </a>
                  );
                })}
                {(asset.public_documents || []).map((d) => (
                  <a
                    key={`doc-${d.id}`}
                    href={publicApi.documentFileUrl(code, d.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between p-3 rounded-lg bg-[#262626] border border-[#2E2E2E] hover:border-[#FFD108]/40 transition-colors"
                    data-testid={`public-doc-${d.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium truncate">{d.name}</p>
                      <p className="text-[hsl(0,0%,63%)] font-mono text-[11px] uppercase">
                        {(d.type || 'document').replace(/_/g, ' ')}
                      </p>
                    </div>
                    <FileText className="w-4 h-4 text-[#FFD108] flex-shrink-0" />
                  </a>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Phase 52: anonymous Inspection runner — only shows when the
            admin has whitelisted at least one template on this asset. */}
        <PublicInspectionRunner code={code} templates={publicInspectionTemplates} />

        {/* Phase 51: AI-powered Error Code Lookup. Renders only when the
            owner-tenant has linked at least one OEM error-code library to
            this asset. Hits the anonymous public lookup endpoint. */}
        {asset.error_code_libraries_count > 0 && (
          <Card className="bg-[#1C1C1C] border-[#2E2E2E] mb-4" data-testid="error-code-lookup-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <Cpu className="w-4 h-4 text-[#FFD108]" />
                <h2 className="label-overline">Error Code Lookup</h2>
              </div>
              <p className="text-[hsl(0,0%,63%)] text-xs mb-3">
                Enter the code shown on the equipment display (e.g. <span className="font-mono text-white">E2847</span>)
                to look up its meaning and fix steps from the OEM service manual.
              </p>
              <form onSubmit={handleErrorCodeLookup} className="flex items-center gap-2">
                <Input
                  value={errorCodeInput}
                  onChange={(e) => setErrorCodeInput(e.target.value.toUpperCase())}
                  placeholder="e.g. E2847"
                  className="bg-[#262626] border-[#2E2E2E] text-white font-mono h-11 uppercase tracking-wider"
                  data-testid="error-code-input"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                />
                <Button
                  type="submit"
                  disabled={errorLookupLoading}
                  className="h-11 btn-volt px-4 flex-shrink-0"
                  data-testid="error-code-lookup-btn"
                >
                  {errorLookupLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                </Button>
              </form>

              {errorLookupError && (
                <p className="mt-3 text-sm text-red-400" data-testid="error-code-lookup-error">
                  {errorLookupError}
                </p>
              )}

              {errorLookupResult && errorLookupResult.found && errorLookupResult.match && (
                <div className="mt-4 p-4 rounded-lg bg-[#262626] border border-[#2E2E2E]" data-testid="error-code-lookup-result">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                      <p className="font-mono text-[#FFD108] text-xl font-bold">{errorLookupResult.match.code}</p>
                      {errorLookupResult.match.title && (
                        <p className="text-white text-sm font-semibold mt-1">{errorLookupResult.match.title}</p>
                      )}
                    </div>
                    {errorLookupResult.match.severity && (
                      <Badge
                        className={`border-0 font-medium ${
                          (severityStyles[errorLookupResult.match.severity?.toLowerCase()] || severityStyles.medium).bg
                        } ${
                          (severityStyles[errorLookupResult.match.severity?.toLowerCase()] || severityStyles.medium).text
                        }`}
                      >
                        {String(errorLookupResult.match.severity).toUpperCase()}
                      </Badge>
                    )}
                  </div>

                  {errorLookupResult.match.meaning && (
                    <div className="mb-3">
                      <p className="label-overline mb-1">What it means</p>
                      <p className="text-white text-sm leading-relaxed">{errorLookupResult.match.meaning}</p>
                    </div>
                  )}

                  {errorLookupResult.match.fix_steps && (
                    <div className="mb-3">
                      <p className="label-overline mb-1">Recommended fix</p>
                      {Array.isArray(errorLookupResult.match.fix_steps) ? (
                        <ol className="text-white text-sm leading-relaxed list-decimal pl-5 space-y-1">
                          {errorLookupResult.match.fix_steps.map((step, i) => (
                            <li key={i}>{step}</li>
                          ))}
                        </ol>
                      ) : (
                        <p className="text-white text-sm leading-relaxed whitespace-pre-line">
                          {errorLookupResult.match.fix_steps}
                        </p>
                      )}
                    </div>
                  )}

                  <p className="text-[hsl(0,0%,63%)] text-[11px] font-mono mt-2">
                    {errorLookupResult.libraries?.[0]?.name || 'OEM Service Manual'}
                    {errorLookupResult.match.source_page ? ` · page ${errorLookupResult.match.source_page}` : ''}
                  </p>
                </div>
              )}

              {errorLookupResult && !errorLookupResult.found && (
                <div className="mt-4 p-4 rounded-lg bg-[#262626] border border-[#2E2E2E]" data-testid="error-code-lookup-empty">
                  <p className="text-amber-400 text-sm font-semibold mb-1">
                    No match for "{errorLookupResult.code}"
                  </p>
                  <p className="text-[hsl(0,0%,63%)] text-xs">
                    {errorLookupResult.reason === 'no_libraries_linked'
                      ? 'No OEM manual is linked to this asset yet. Contact the asset owner.'
                      : `Searched ${errorLookupResult.libraries?.length || 0} linked manual${(errorLookupResult.libraries?.length || 0) === 1 ? '' : 's'}. Double-check the code or use the report-issue form below.`}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Report Issue Section */}
        {submitted ? (
          <Card className="bg-green-900/20 border-green-500/30 mb-4" data-testid="report-success">
            <CardContent className="p-5 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h2 className="text-lg font-heading font-semibold text-green-400 mb-1">Issue Reported</h2>
              <p className="text-[hsl(0,0%,63%)] text-sm mb-2">Thank you for your report.</p>
              <p className="font-mono text-[#FFD108] text-sm font-bold">{defectNumber}</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-[#1C1C1C] border-[#2E2E2E]" data-testid="report-issue-card">
            <CardContent className="p-5">
              <button
                onClick={() => setShowReport(!showReport)}
                className="w-full flex items-center justify-between"
                data-testid="report-issue-toggle"
              >
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-[#FF4F00]" />
                  <span className="font-heading font-semibold text-white">Report an Issue</span>
                </div>
                {showReport ? (
                  <ChevronUp className="w-5 h-5 text-[hsl(0,0%,63%)]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[hsl(0,0%,63%)]" />
                )}
              </button>

              {showReport && (
                <form onSubmit={handleSubmitReport} className="mt-4 space-y-4" data-testid="report-issue-form">
                  <div className="space-y-2">
                    <Label className="label-overline">Title *</Label>
                    <Input
                      value={reportTitle}
                      onChange={e => setReportTitle(e.target.value)}
                      placeholder="Brief description of the issue..."
                      className="bg-[#262626] border-[#2E2E2E] text-white h-11"
                      data-testid="report-title-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="label-overline">Severity *</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {['low', 'medium', 'critical'].map(s => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setReportSeverity(s)}
                          data-testid={`report-severity-${s}`}
                          className={`py-2.5 rounded-md text-xs font-medium border transition-all uppercase tracking-wide ${
                            reportSeverity === s
                              ? `${severityStyles[s].bg} ${severityStyles[s].text} ${severityStyles[s].border}`
                              : 'border-[#2E2E2E] bg-[#262626] text-[hsl(0,0%,63%)]'
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="label-overline">Description *</Label>
                    <Textarea
                      value={reportDesc}
                      onChange={e => setReportDesc(e.target.value)}
                      placeholder="Detailed description of the issue..."
                      className="bg-[#262626] border-[#2E2E2E] text-white min-h-[100px]"
                      data-testid="report-description-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="label-overline">Your Name *</Label>
                      <Input
                        value={reporterName}
                        onChange={e => setReporterName(e.target.value)}
                        placeholder="Full name"
                        required
                        className="bg-[#262626] border-[#2E2E2E] text-white h-10"
                        data-testid="report-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="label-overline">Mobile Phone *</Label>
                      <Input
                        type="tel"
                        inputMode="tel"
                        autoComplete="tel"
                        value={reporterPhone}
                        onChange={e => setReporterPhone(e.target.value)}
                        placeholder="0414 320 847"
                        required
                        className="bg-[#262626] border-[#2E2E2E] text-white h-10"
                        data-testid="report-phone-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-overline">Your Email (optional)</Label>
                    <Input
                      type="email"
                      autoComplete="email"
                      value={reporterEmail}
                      onChange={e => setReporterEmail(e.target.value)}
                      placeholder="you@example.com"
                      className="bg-[#262626] border-[#2E2E2E] text-white h-10"
                      data-testid="report-email-input"
                    />
                  </div>

                  {/* Photos */}
                  <div className="space-y-2">
                    <Label className="label-overline">Photos</Label>
                    <div className="flex flex-wrap gap-2">
                      {photos.map((photo, idx) => (
                        <div key={idx} className="relative w-16 h-16">
                          <img src={photo} alt="" className="w-full h-full object-cover rounded-md" />
                          <button
                            type="button"
                            onClick={() => setPhotos(prev => prev.filter((_, i) => i !== idx))}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center"
                          >
                            <X className="w-3 h-3 text-white" />
                          </button>
                        </div>
                      ))}
                      <label className="w-16 h-16 border border-dashed border-[#2E2E2E] rounded-md flex items-center justify-center text-[hsl(0,0%,63%)] hover:border-[hsl(0,0%,63%)] cursor-pointer transition-colors">
                        <Camera className="w-5 h-5" />
                        <input
                          type="file"
                          accept="image/*"
                          capture="environment"
                          multiple
                          onChange={handlePhotoCapture}
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full h-12 btn-volt"
                    data-testid="submit-report-btn"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        SUBMIT REPORT
                      </>
                    )}
                  </Button>
                </form>
              )}
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-[hsl(0,0%,63%)] text-xs mt-6">
          Powered by EQUIPR Guardian
        </p>
      </div>
    </div>
  );
};

export default PublicAssetPage;
